import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { userAPI } from '../../profile/api/userAPI';
import { PushSettings } from '../types/PushSettings';
import { custonSooner } from '../../../components/CustopmSooner';

/**
 * Каналы уведомлений: чтение из профиля, запись в `/user/save-settings`.
 *
 * Раньше флаг жил в localStorage и до бэкенда не доезжал — на другом
 * устройстве настройка не применялась, а сервер продолжал слать всё подряд.
 * Источник правды теперь один, серверный.
 */

/** Ничего не пришло — считаем, что подписан: так же ведёт себя бэкенд. */
const DEFAULTS: PushSettings = {
  push_notification: true,
  tg_notification: true,
  sms_notification: true,
};

const toBool = (value: unknown, fallback: boolean) =>
  typeof value === 'boolean' ? value : fallback;

/**
 * `/user/me` отдаёт флаги полем `push_settings` — JSON-строкой внутри JSON.
 * На всякий случай принимаем и уже разобранный объект: если бэкенд однажды
 * начнёт отдавать его нормально, экран не сломается.
 */
export const parsePushSettings = (raw: unknown): PushSettings => {
  let value = raw;

  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return DEFAULTS;
    }
  }

  if (!value || typeof value !== 'object') return DEFAULTS;

  const source = value as Record<string, unknown>;

  return {
    push_notification: toBool(
      source.push_notification,
      DEFAULTS.push_notification
    ),
    tg_notification: toBool(source.tg_notification, DEFAULTS.tg_notification),
    sms_notification: toBool(
      source.sms_notification,
      DEFAULTS.sms_notification
    ),
  };
};

export const usePushSettings = () => {
  const token = localStorage.getItem('token');

  // Ключ намеренно тот же, что у useGetUserProfile: профиль уже загружается
  // приложением, и второй запрос за теми же данными не нужен — react-query
  // отдаст кэш, а `select` разберёт из него только настройки.
  const { data, isLoading } = useQuery({
    queryKey: ['user-profile', token],
    queryFn: async () => await userAPI.getProfile(),
    select: response => parsePushSettings(response?.data?.push_settings),
    enabled: Boolean(token),
  });

  return {
    settings: data ?? DEFAULTS,
    isLoading: isLoading && Boolean(token),
  };
};

export const useSavePushSettings = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (body: PushSettings) => {
      const res = await userAPI.savePushSettings(body);

      // Конвенция этого API: отказ приходит с HTTP 200 и success: false.
      // Сравниваем строго с false — у ответа без поля поведение прежнее.
      if (res?.data?.success === false) {
        throw new Error(res?.data?.message ?? 'Не удалось сохранить настройки');
      }

      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ??
        (error as Error)?.message ??
        'Не удалось сохранить настройки';

      custonSooner.error(message);
    },
  });

  return { savePushSettings: mutateAsync, isSaving: isPending };
};
